import getTextColor from '@dashboard/common/utils/getTextColor'
import {
  editableSectionClasses
} from 'features/EmailEditor/constants'
import { getTemplate, resolveEditorTemplateKey } from 'features/EmailEditor/templates'
import {
  extractPreviewBodyHtml,
  extractSocialMediaUrls,
  generateSocialMediaLinksHtml,
  getHtmlFromMjml,
  mergeNotificationEmailPayload,
  normalizeEmailFooterFields,
  prepareEditorialEmailHtml,
  replaceTagsWithPreviewContent,
  resolveSocialFooterTheme,
  resolveStoredShellTemplateKey
} from 'features/EmailEditor/utils'
import useBrand from 'hooks/useBrand'
import { applyLogoDimensions, calculateLogoDimensions, getTemplateLogoLimits } from 'features/EmailEditor/emailLogoHelpers'
import {
  applyUserEditedImageFields,
  buildEditorTemplateVariables,
  LOCAL_PRESERVE_IMAGE_FIELD_KEYS,
  mergeLoadedEmailState,
  resolveEditorImageField
} from 'features/EmailEditor/emailImagePlaceholders'
import {
  buildRichtextHtmlMap,
  mergeDesignRichtextFields,
  mergeTemplateSwitchRichtextFields,
  renderCustomizationBodyHtml,
  resolveCampaignBodyShellHtml,
  resolveEditorRichtextFields
} from 'features/EmailEditor/campaignEmailCustomizationHelpers'
import { renderCampaignCustomizationHtml } from 'features/EmailEditor/inviteEmailTemplateBodyInjector'
import { syncTemplateHeroImageUpdate } from 'features/EmailEditor/templateHeroFields'
import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router'
import campaignApi from 'services/api/campaign'
import inviteEmailDesignApi from 'services/api/inviteEmailDesign'
import { invalidateInviteEmailDesignPreviewCache } from 'features/InviteEmailDesign/useInviteEmailDesignPreview'
import hotelApi from 'services/api/hotel'
import { getCleanUrl } from 'utils/common'
import { formatPhoneNumber } from 'utils/helpers'

import styles from './EmailEditor.module.scss'

const defaultLogoDimensions = { width: '120px', height: '60px' }

function getCountryName(countryCode) {
  if (!countryCode) return null

  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(countryCode.toUpperCase())
  } catch {
    return countryCode
  }
}

function buildHotelWebsiteUrl(primaryWebsiteUrl) {
  if (!primaryWebsiteUrl) return null

  const trimmed = primaryWebsiteUrl.trim()
  return trimmed.startsWith('http://') || trimmed.startsWith('https://')
    ? trimmed
    : `https://${trimmed}`
}

function buildHotelWebsiteDisplay(primaryWebsiteUrl) {
  const cleanUrl = getCleanUrl(buildHotelWebsiteUrl(primaryWebsiteUrl))
  if (!cleanUrl) return null

  return cleanUrl.startsWith('www.') ? cleanUrl.slice(4) : cleanUrl
}

function buildFullHotelAddress(subsidiary) {
  const parts = [
    subsidiary?.streetAddress,
    subsidiary?.city,
    subsidiary?.stateCode,
    subsidiary?.postalCode,
    getCountryName(subsidiary?.countryCode)
  ].filter(Boolean)

  return parts.length ? parts.join(', ') : null
}

async function resolveLogoDimensions(logoUrl, templateKey = 'default') {
  if (!logoUrl) {
    return { ...defaultLogoDimensions, loaded: false }
  }

  return calculateLogoDimensions(logoUrl, getTemplateLogoLimits(templateKey))
}

const EmailEditorContext = createContext()

export const useEmailEditor = () => {
  const context = useContext(EmailEditorContext)
  if (!context) {
    throw new Error('useEmailEditor must be used within an EmailEditorProvider')
  }
  return context
}

export const EmailEditorProvider = ({
  children,
  contentId,
  contentCategory,
  mode = 'campaign',
  designTemplateId,
  readOnly = false
}) => {
  const { id: routeId } = useParams()
  const id = contentId ?? routeId
  const isDesignMode = mode === 'design'
  const isCampaignBodyMode = mode === 'campaignBody'
  const resolvedDesignTemplateId = designTemplateId ?? (isDesignMode ? routeId : null)

  const [editingSection, setEditingSection] = useState('inbox-preview')
  const [selectedTemplate, setSelectedTemplate] = useState('default')
  const [hoveredPreview, setHoveredPreview] = useState(null)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(true)
  const [isEditorDataReady, setIsEditorDataReady] = useState(!isDesignMode)
  const [editorHydrationKey, setEditorHydrationKey] = useState(0)
  const [emailHtml, setEmailHtml] = useState()
  const [previewHtml, setPreviewHtml] = useState()
  const [campaignShellHtml, setCampaignShellHtml] = useState(null)

  const userName = useSelector(s => s.login.currentUser.name)
  const currentUser = useSelector(s => s.login.currentUser)
  const currentHotelId = useSelector(s => s.login.currentHotelId)
  const { colors, logoUrl: brandDefaultLogoUrl } = useBrand()

  const isInternalTemplateChangeRef = useRef(false)
  const emailDataLoadKeyRef = useRef(null)
  const emailDataLoadInFlightRef = useRef(null)
  const userEditedImageFieldsRef = useRef({})
  const [userEditedImageFields, setUserEditedImageFields] = useState({})

  const bumpEditorHydration = () => {
    setEditorHydrationKey(key => key + 1)
  }

  // Get current template and its default parameters
  const currentTemplate = getTemplate(selectedTemplate)
  const [emailState, setEmailState] = useState(currentTemplate.params)

  const sections = currentTemplate.sections

  // Function to scroll to a section in the preview
  const scrollToSection = sectionClass => {
    const selector = sectionClass.startsWith('.') ? sectionClass : `.mjml-${sectionClass}`
    const targets = document.querySelectorAll(selector)
    const targetElement = targets.length ? targets[targets.length - 1] : null

    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      })

      // Add highlight effect
      document.querySelectorAll(`.${styles.highlightedSection}`).forEach(el => {
        el.classList.remove(styles.highlightedSection)
      })
      targetElement.classList.add(styles.highlightedSection)
    }
  }

  // Reset design editor hydration when switching templates.
  useEffect(() => {
    if (!isDesignMode || !resolvedDesignTemplateId) {
      return
    }

    setIsEditorDataReady(false)
    setPreviewHtml('')
    emailDataLoadKeyRef.current = null
    emailDataLoadInFlightRef.current = null
  }, [isDesignMode, resolvedDesignTemplateId])

  // Load campaign or design template email data and subsidiary address details
  useEffect(() => {
    const loadKey = isDesignMode
      ? `design:${resolvedDesignTemplateId}`
      : `campaign:${id}`

    if (isDesignMode && resolvedDesignTemplateId) {
      if (
        emailDataLoadKeyRef.current !== loadKey &&
        emailDataLoadInFlightRef.current !== loadKey
      ) {
        emailDataLoadInFlightRef.current = loadKey

        inviteEmailDesignApi
          .getTemplate({ templateId: resolvedDesignTemplateId })
          .then(emailData => {
            const shellTemplateKey =
              resolveStoredShellTemplateKey(emailData?.emailParams) ||
              emailData?.shellTemplateKey
            const resolvedTemplateKey = shellTemplateKey
              ? resolveEditorTemplateKey(shellTemplateKey)
              : selectedTemplate

            if (shellTemplateKey) {
              isInternalTemplateChangeRef.current = true
              setSelectedTemplate(resolvedTemplateKey)
            }

            setEmailState(prevState => {
              const loadedState = mergeNotificationEmailPayload(
                getTemplate(resolvedTemplateKey).params,
                {
                  ...emailData.emailParams,
                  richtextFields: mergeDesignRichtextFields(
                    getTemplate(resolvedTemplateKey).params,
                    emailData.emailParams?.richtextFields || {}
                  )
                },
                emailData.globalParams,
                { brandLogoUrl: brandDefaultLogoUrl }
              )

              return mergeLoadedEmailState(
                prevState,
                loadedState,
                brandDefaultLogoUrl,
                userEditedImageFieldsRef.current
              )
            })
            emailDataLoadKeyRef.current = loadKey
            setHasUnsavedChanges(false)
            setIsEditorDataReady(true)
            bumpEditorHydration()
          })
          .catch(err => {
            console.error(err)
          })
          .finally(() => {
            if (emailDataLoadInFlightRef.current === loadKey) {
              emailDataLoadInFlightRef.current = null
            }

            setIsEditorDataReady(true)
          })
      }
    } else if (id) {
      if (
        emailDataLoadKeyRef.current !== loadKey &&
        emailDataLoadInFlightRef.current !== loadKey
      ) {
        emailDataLoadInFlightRef.current = loadKey

        campaignApi
        .getCampaignEmail({ campaignId: id })
        .then(emailData => {
          const shellTemplateKey = resolveStoredShellTemplateKey(emailData?.emailParams)
          const resolvedTemplateKey = shellTemplateKey
            ? resolveEditorTemplateKey(shellTemplateKey)
            : selectedTemplate

          if (shellTemplateKey) {
            isInternalTemplateChangeRef.current = true
            setSelectedTemplate(resolvedTemplateKey)
          }

          const mergedState = mergeNotificationEmailPayload(
            getTemplate(resolvedTemplateKey).params,
            {
              ...emailData?.emailParams,
              richtextFields: mergeDesignRichtextFields(
                getTemplate(resolvedTemplateKey).params,
                emailData?.emailParams?.richtextFields || {}
              )
            },
            emailData?.globalParams,
            { brandLogoUrl: brandDefaultLogoUrl }
          )

          if (isCampaignBodyMode) {
            return resolveCampaignBodyShellHtml(emailData).then(shellHtml => {
              if (shellHtml) {
                setCampaignShellHtml(shellHtml)
              }

              return applyLogoDimensions(mergedState, brandDefaultLogoUrl)
            })
          }

          return applyLogoDimensions(mergedState, brandDefaultLogoUrl)
        })
        .then(newState => {
          if (!newState) {
            return
          }

          setEmailState(prevState =>
            mergeLoadedEmailState(
              prevState,
              newState,
              brandDefaultLogoUrl,
              userEditedImageFieldsRef.current
            )
          )
          emailDataLoadKeyRef.current = loadKey
          setHasUnsavedChanges(false)
          bumpEditorHydration()
        })
        .catch(err => {
          console.error(err)
        })
        .finally(() => {
          if (emailDataLoadInFlightRef.current === loadKey) {
            emailDataLoadInFlightRef.current = null
          }
        })
      }
    }

    if (currentHotelId) {
      hotelApi
        .getDetails(currentHotelId)
        .then(({ subsidiary }) => {
          if (!subsidiary) return

          const updates = {}

          if (subsidiary.reservationHotLine) {
            updates.hotelPhone = formatPhoneNumber(subsidiary.reservationHotLine)
          }

          if (subsidiary.primaryWebsiteUrl) {
            updates.hotelWebsiteUrl = buildHotelWebsiteUrl(subsidiary.primaryWebsiteUrl)
            updates.hotelWebsite = buildHotelWebsiteDisplay(subsidiary.primaryWebsiteUrl)
          }

          const fullAddress = buildFullHotelAddress(subsidiary)
          if (subsidiary.hotelName) {
            updates.hotelName = subsidiary.hotelName
          }

          if (fullAddress) {
            updates.hotelAddress = fullAddress
            updates.hotelCityStateZip = ''
          } else if (subsidiary.city || subsidiary.stateCode || subsidiary.postalCode) {
            updates.hotelCityStateZip = [
              subsidiary.city,
              [subsidiary.stateCode, subsidiary.postalCode].filter(Boolean).join(' ')
            ]
              .filter(Boolean)
              .join(', ')
          }

          if (Object.keys(updates).length) {
            setEmailState(prevState => ({
              ...prevState,
              ...updates
            }))
          }
        })
        .catch(err => {
          console.error(err)
        })
    }
  }, [id, currentHotelId, isDesignMode, resolvedDesignTemplateId, isCampaignBodyMode])

  function refreshEmailData() {
    emailDataLoadKeyRef.current = null
    emailDataLoadInFlightRef.current = null
    setUserEditedImageFields({})
    userEditedImageFieldsRef.current = {}
    const themeDefault = getTemplate(currentTemplate)
    const loadKey = isDesignMode
      ? `design:${resolvedDesignTemplateId}`
      : `campaign:${id}`

    if (isDesignMode && resolvedDesignTemplateId) {
      setIsEditorDataReady(false)
      setPreviewHtml('')

      inviteEmailDesignApi
        .getTemplate({ templateId: resolvedDesignTemplateId })
        .then(emailData => {
          const shellTemplateKey =
            resolveStoredShellTemplateKey(emailData?.emailParams) ||
            emailData?.shellTemplateKey
          const resolvedTemplateKey = shellTemplateKey
            ? resolveEditorTemplateKey(shellTemplateKey)
            : selectedTemplate

          if (shellTemplateKey) {
            isInternalTemplateChangeRef.current = true
            setSelectedTemplate(resolvedTemplateKey)
          }

          const themeParams = getTemplate(resolvedTemplateKey).params

          return {
            ...themeParams,
            ...emailData.emailParams,
            ...emailData.globalParams,
            shellTemplateKey: resolvedTemplateKey,
            selectedTemplate: resolvedTemplateKey,
            richtextFields: mergeDesignRichtextFields(
              themeParams,
              emailData.emailParams?.richtextFields || {}
            )
          }
        })
        .then(newState => {
          const logoCandidate = newState.logoUrl || brandDefaultLogoUrl
          const templateKey = newState.selectedTemplate || newState.shellTemplateKey || selectedTemplate

          resolveLogoDimensions(logoCandidate, templateKey).then(dimensions => {
            setEmailState(prevState =>
              mergeLoadedEmailState(
                prevState,
                {
                  ...newState,
                  logoUrl: logoCandidate,
                  logoWidth: dimensions.width,
                  logoHeight: dimensions.height
                },
                brandDefaultLogoUrl,
                userEditedImageFieldsRef.current
              )
            )
            emailDataLoadKeyRef.current = loadKey
            setHasUnsavedChanges(false)
            setIsEditorDataReady(true)
            bumpEditorHydration()
          })
        })
        .catch(err => {
          console.error(err)
        })
        .finally(() => {
          setIsEditorDataReady(true)
        })
      return
    }

    if (id) {
      campaignApi
        .getCampaignEmail({ campaignId: id })
        .then(emailData => {
          const shellTemplateKey = resolveStoredShellTemplateKey(emailData?.emailParams)
          const resolvedTemplateKey = shellTemplateKey
            ? resolveEditorTemplateKey(shellTemplateKey)
            : selectedTemplate

          if (shellTemplateKey) {
            isInternalTemplateChangeRef.current = true
            setSelectedTemplate(resolvedTemplateKey)
          }

          const mergedState = mergeNotificationEmailPayload(
            getTemplate(resolvedTemplateKey).params,
            {
              ...emailData?.emailParams,
              richtextFields: mergeDesignRichtextFields(
                getTemplate(resolvedTemplateKey).params,
                emailData?.emailParams?.richtextFields || {}
              )
            },
            emailData?.globalParams,
            { brandLogoUrl: brandDefaultLogoUrl }
          )

          if (isCampaignBodyMode) {
            return resolveCampaignBodyShellHtml(emailData).then(shellHtml => {
              if (shellHtml) {
                setCampaignShellHtml(shellHtml)
              }

              return applyLogoDimensions(mergedState, brandDefaultLogoUrl)
            })
          }

          return applyLogoDimensions(mergedState, brandDefaultLogoUrl)
        })
        .then(newState => {
          if (!newState) {
            return
          }

          if (isCampaignBodyMode) {
            setEmailState(prevState =>
              mergeLoadedEmailState(
                prevState,
                newState,
                brandDefaultLogoUrl,
                userEditedImageFieldsRef.current
              )
            )
            emailDataLoadKeyRef.current = loadKey
            setHasUnsavedChanges(false)
            bumpEditorHydration()
            return
          }

          setEmailState(prevState =>
            mergeLoadedEmailState(
              prevState,
              newState,
              brandDefaultLogoUrl,
              userEditedImageFieldsRef.current
            )
          )
          emailDataLoadKeyRef.current = loadKey
          setHasUnsavedChanges(false)
          bumpEditorHydration()
        })
        .catch(err => {
          console.error(err)
        })
    }
  }

  // Logo handling
  useLayoutEffect(() => {
    const logoUrl = emailState.logoUrl
    if (!logoUrl) return

    let isCancelled = false

    resolveLogoDimensions(logoUrl, selectedTemplate).then(dimensions => {
      if (isCancelled) {
        return
      }

      setEmailState(prevState => {
        if (prevState.logoUrl !== logoUrl) {
          return prevState
        }

        return {
          ...prevState,
          logoWidth: dimensions.width,
          logoHeight: dimensions.height
        }
      })
    })

    return () => {
      isCancelled = true
    }
  }, [emailState.logoUrl, selectedTemplate])

  useEffect(() => {
    setEmailState(prevState => {
      const resolvedRichtextFields = resolveEditorRichtextFields(
        currentTemplate.params,
        prevState.richtextFields || {}
      )
      const currentRichtextFields = prevState.richtextFields || {}
      const storyKeys = ['col1Html', 'col2Html', 'col3Html', 'finePrintHtml', 'personalHtml']
      const needsUpdate = storyKeys.some(key => {
        const resolvedValue = `${resolvedRichtextFields[key] || ''}`.trim()
        const currentValue = `${currentRichtextFields[key] || ''}`.trim()

        return resolvedValue && resolvedValue !== currentValue
      })

      if (!needsUpdate) {
        return prevState
      }

      return {
        ...prevState,
        richtextFields: {
          ...currentRichtextFields,
          ...resolvedRichtextFields
        }
      }
    })
  }, [selectedTemplate, editorHydrationKey, currentTemplate.params])

  // Brand colors and logo setup
  useEffect(() => {
    setEmailState(prevState => {
      const nextState = { ...prevState }
      let hasChanges = false

      if (!prevState.logoUrl && brandDefaultLogoUrl) {
        nextState.logoUrl = brandDefaultLogoUrl
        hasChanges = true
      }

      if (prevState.headerBgColor === 'transparent') {
        nextState.headerBgColor = colors.primary
        hasChanges = true
      }

      if (prevState.ctaBackgroundColor === 'transparent') {
        nextState.ctaBackgroundColor = colors.primary
        hasChanges = true
      }

      return hasChanges ? nextState : prevState
    })
  }, [colors, brandDefaultLogoUrl])

  // CTA text color update (solid-fill buttons only; editorial outline pills keep user text color)
  useEffect(() => {
    if (selectedTemplate !== 'editorialEvent') {
      return
    }

    const borderColor = `${emailState.ctaBackgroundColor || ''}`.trim()
    const textColor = `${emailState.ctaTextColor || ''}`.trim()

    if (
      borderColor &&
      (!textColor || textColor === '#000000' || textColor === '#ffffff')
    ) {
      updateEmailState('ctaTextColor', borderColor)
    }
  }, [emailState.ctaBackgroundColor, emailState.ctaTextColor, selectedTemplate])

  useEffect(() => {
    if (selectedTemplate === 'editorialEvent') {
      return
    }

    const newCtaTextColor = getTextColor(emailState.ctaBackgroundColor)
    if (newCtaTextColor !== emailState.ctaTextColor) {
      updateEmailState('ctaTextColor', newCtaTextColor)
    }
  }, [emailState.ctaBackgroundColor, emailState.ctaTextColor, selectedTemplate])

  // Generate email HTML
  useEffect(() => {
    if (isDesignMode && !isEditorDataReady) {
      return
    }

    if (isCampaignBodyMode && campaignShellHtml) {
      const bodyHtml = renderCustomizationBodyHtml(emailState.richtextFields?.bodyHtml)
      const html = renderCampaignCustomizationHtml(campaignShellHtml, {
        emailTitle: emailState.emailTitle,
        bodyHtml,
        ctaText: emailState.ctaText
      })

      if (editingSection && !isCampaignBodyMode) {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = extractPreviewBodyHtml(html)
        const sectionElement = tempDiv.querySelector(`.mjml-${editingSection}`)
        sectionElement && sectionElement.classList.add(styles.highlightedSection)
        setPreviewHtml(
          replaceTagsWithPreviewContent(tempDiv.innerHTML, userName, emailState.hotelName)
        )
      } else {
        setPreviewHtml(
          replaceTagsWithPreviewContent(
            extractPreviewBodyHtml(html),
            userName,
            emailState.hotelName
          )
        )
      }

      setEmailHtml(html)
      return
    }

    const socialUrls = extractSocialMediaUrls(emailState.socialMediaLinks)
    const templateVariables = normalizeEmailFooterFields(
      buildEditorTemplateVariables(emailState, {
        brandLogoUrl: brandDefaultLogoUrl,
        userImageOverrides: userEditedImageFieldsRef.current,
        socialUrls
      })
    )

    const resolvedRichtextFields = mergeDesignRichtextFields(
      currentTemplate.params,
      emailState.richtextFields || {}
    )

    let html = getHtmlFromMjml(
      currentTemplate.template,
      templateVariables,
      {
        ...buildRichtextHtmlMap(resolvedRichtextFields),
        socialMediaLinksHtml: generateSocialMediaLinksHtml(
          emailState.socialMediaLinks,
          ...Object.values(resolveSocialFooterTheme(selectedTemplate))
        )
      },
      { templateKey: selectedTemplate, brandLogoUrl: brandDefaultLogoUrl }
    )

    if (editingSection && !isCampaignBodyMode) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = extractPreviewBodyHtml(html)
      const sectionElement = tempDiv.querySelector(`.mjml-${editingSection}`)
      sectionElement && sectionElement.classList.add(styles.highlightedSection)
      setPreviewHtml(
        replaceTagsWithPreviewContent(tempDiv.innerHTML, userName, emailState.hotelName)
      )
    } else {
      setPreviewHtml(
        replaceTagsWithPreviewContent(
          extractPreviewBodyHtml(html),
          userName,
          emailState.hotelName
        )
      )
    }

    setEmailHtml(
      prepareEditorialEmailHtml(html, selectedTemplate, {
        ...emailState,
        ctaBackgroundColor: emailState.ctaBackgroundColor || templateVariables.ctaBackgroundColor,
        ctaTextColor:
          emailState.ctaTextColor ||
          templateVariables.ctaTextColor ||
          emailState.ctaBackgroundColor ||
          templateVariables.ctaBackgroundColor
      })
    )
  }, [
    emailState,
    editingSection,
    userName,
    currentTemplate.template,
    selectedTemplate,
    isCampaignBodyMode,
    campaignShellHtml,
    brandDefaultLogoUrl,
    userEditedImageFields,
    isDesignMode,
    isEditorDataReady
  ])

  // State update functions
  const updateEmailState = (property, value) => {
    if (LOCAL_PRESERVE_IMAGE_FIELD_KEYS.includes(property)) {
      const nextOverrides = {
        ...userEditedImageFieldsRef.current,
        [property]: value
      }
      userEditedImageFieldsRef.current = nextOverrides
      setUserEditedImageFields(nextOverrides)
    }

    setEmailState(prevState => {
      const templateKey =
        prevState.selectedTemplate || prevState.shellTemplateKey || selectedTemplate
      const syncedValues = syncTemplateHeroImageUpdate(property, value, templateKey)

      return {
        ...prevState,
        ...syncedValues
      }
    })
    setHasUnsavedChanges(true)
  }

  const resolveEditorImageFieldForPanel = property =>
    resolveEditorImageField(emailState, property, userEditedImageFieldsRef.current)

  const updateRichTextField = (field, value) => {
    setEmailState(prevState => ({
      ...prevState,
      richtextFields: {
        ...prevState.richtextFields,
        [field]: value
      }
    }))
    setHasUnsavedChanges(true)
  }

  // Handle template change
  const handleTemplateChange = (templateName, { markDirty = true } = {}) => {
    const resolvedTemplateName = resolveEditorTemplateKey(templateName)

    setSelectedTemplate(resolvedTemplateName)
    const newTemplate = getTemplate(resolvedTemplateName)
    isInternalTemplateChangeRef.current = true

    setEmailState(prevState => {
      const preservedLogoUrl = prevState.logoUrl || brandDefaultLogoUrl || ''

      return {
        ...newTemplate.params,
        ...prevState,
        logoUrl: preservedLogoUrl,
        selectedTemplate: resolvedTemplateName,
        shellTemplateKey: resolvedTemplateName,
        richtextFields: mergeTemplateSwitchRichtextFields(
          newTemplate.params,
          prevState.richtextFields || {}
        )
      }
    })

    if (markDirty) {
      setHasUnsavedChanges(true)
    }
  }

  // Handle social media links
  const handleSocialMediaChange = (index, field, value) => {
    const updatedLinks = [...emailState.socialMediaLinks]
    updatedLinks[index][field] = value
    updateEmailState('socialMediaLinks', updatedLinks)
  }

  // Handle click on email preview
  const handleClick = e => {
    if (e.target.tagName === 'A' || e.target.closest('a')) {
      e.preventDefault()
    }

    let target = e.target.closest(editableSectionClasses.map(c => `.${c}`).join(','))

    if (!target) {
      return
    }

    const targetName = [...target.classList].find(c => c.startsWith('mjml-'))
    const section = targetName?.replace('mjml-', '')

    if (isCampaignBodyMode && section !== 'body') {
      return
    }

    scrollToSection(`.${targetName}`)

    document.querySelectorAll(`.${styles.highlightedSection}`).forEach(el => {
      el.classList.remove(styles.highlightedSection)
    })
    target &&
      !target.classList.contains(styles.highlightedSection) &&
      target.classList.add(styles.highlightedSection)

    if (target) {
      setEditingSection(section)
    }
  }

  // Save changes function
  const saveChanges = () => {
    if (readOnly) {
      return Promise.resolve()
    }

    const mergedEmailState = {
      ...applyUserEditedImageFields(emailState, userEditedImageFieldsRef.current),
      richtextFields: resolveEditorRichtextFields(
        currentTemplate.params,
        emailState.richtextFields || {}
      )
    }

    // eslint-disable-next-line no-unused-vars
    const {
      termsUrl,
      privacyPolicyUrl,
      hotelName,
      hotelAddress,
      hotelCityStateZip,
      hotelPhone,
      hotelWebsite,
      hotelWebsiteUrl,
      socialMediaLinks,
      ...emailParams
    } = mergedEmailState

    if (isDesignMode && resolvedDesignTemplateId) {
      return inviteEmailDesignApi
        .updateTemplate({
          templateId: resolvedDesignTemplateId,
          emailParams: {
            ...emailParams,
            shellTemplateKey: selectedTemplate,
            selectedTemplate
          },
          html: emailHtml,
          globalParams: { socialMediaLinks }
        })
        .then(() => {
          invalidateInviteEmailDesignPreviewCache(resolvedDesignTemplateId)
          setHasUnsavedChanges(false)
        })
    }

    if (isCampaignBodyMode) {
      return campaignApi
        .updateCampaignEmail({
          campaignId: id,
          emailParams,
          html: emailHtml,
          globalParams: {}
        })
        .then(() => {
          setHasUnsavedChanges(false)
        })
    }

    return campaignApi
      .updateCampaignEmail({
        campaignId: id,
        emailParams,
        html: emailHtml,
        globalParams: { socialMediaLinks }
      })
      .then(() => {
        setHasUnsavedChanges(false)
      })
  }

  const sendTestEmail = (email = currentUser.email) => {
    const numericContentId =
      id != null && id !== '' ? Number(id) : undefined
    const contentIdForRequest =
      numericContentId !== undefined && !Number.isNaN(numericContentId)
        ? numericContentId
        : undefined

    return campaignApi.sendTestEmail({
      contentCategory,
      contentId: contentIdForRequest,
      email,
      emailBodyHtml: emailHtml,
      emailSubject: emailState.subject
    })
  }

  const value = {
    // State
    editingSection,
    setEditingSection,
    selectedTemplate,
    setSelectedTemplate: handleTemplateChange,
    hoveredPreview,
    setHoveredPreview,
    mousePosition,
    setMousePosition,
    emailState,
    setEmailState,
    currentTemplate,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    isEditorDataReady,
    editorHydrationKey,
    emailHtml,
    previewHtml,
    currentUser,
    id,
    sections,
    mode,
    isDesignMode,
    isCampaignBodyMode,
    readOnly,
    userEditedImageFields,
    resolveEditorImageField: resolveEditorImageFieldForPanel,
    // Functions
    updateEmailState,
    updateRichTextField,
    handleTemplateChange,
    handleSocialMediaChange,
    handleClick,
    saveChanges,
    sendTestEmail,
    refreshEmailData
  }

  return (
    <EmailEditorContext.Provider value={value}>{children}</EmailEditorContext.Provider>
  )
}
